import { useMemo } from 'react';
import { TextField } from '../../../../shared/components/TextField';
import { Snackbar } from '../../../../shared/components/Snackbar';
import { useEditAdmin } from '../../hooks/useEditAdmin';
import Loader from '../../../../app/routes/Loader';
import { ToggleButton } from '@/shared/components/ToggleButton';
import { UpdateStatusDialog } from '../adminList/UpdateStatusDialog';
import { getAdminStatusDialogConfig } from '../adminList/adminStatusDialogConfig';
import CreateLayout from '@/shared/layout/createLayout';
import type { AddUserDetails } from '../../types/addUserType';
import { translateRole } from '@/shared/utils/roleTranslation';

type EditAdminFormProps = {
  formData: AddUserDetails;
  errors: AddUserDetails;
  isSubmitting: boolean;
  adminId: string;
  createdAt: string;
  handleNameChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handlePhoneChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleAddress1Change: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleAddress2Change: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleCityChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleStateChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleZipChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
};

const EditAdmin = () => {
  const {
    formData,
    errors,
    isLoading,
    isSubmitting,
    snackbarError,
    isSnackbarOpen,
    handleCloseSnackbar,
    handleNameChange,
    handlePhoneChange,
    handleAddress1Change,
    handleAddress2Change,
    handleCityChange,
    handleStateChange,
    handleZipChange,
    handleSubmit,
    handleCancel,
    isCancelModalOpen,
    handleCloseCancelModal,
    handleConfirmCancel,
    status,
    handleStatusChange,
    isStatusDialogOpen,
    handleCloseStatusDialog,
    handleConfirmStatusUpdate,
    isStatusSubmitting,
    showStatusSuccessSnackbar,
    statusSuccessMessage,
    handleCloseStatusSuccessSnackbar,
    showStatusErrorSnackbar,
    statusErrorMessage,
    handleCloseStatusErrorSnackbar,
    adminId,
    createdAt,
    hasChanges,
  } = useEditAdmin();

  const EditAdminHeader = () => {
    return (
      <div className="flex items-center justify-between">
        <p className="text-titleLarge text-neutral-10">Edytuj Administratora</p>
        <div className="flex items-center gap-2">
          <p className="text-[12px] text-on-surface-variant">Status:</p>
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleStatusChange();
            }}
            className="cursor-pointer flex min-w-[110px] items-center"
          >
            <div style={{ pointerEvents: 'none' }} className="leading-none">
              <ToggleButton
                checked={status === 'active'}
                onChange={() => {
                  // Prevent toggle from changing - only open dialog
                }}
                label={status === 'active' ? 'Active' : 'Inactive'}
                size="XSmall"
                disabled={isSubmitting || isLoading || isStatusSubmitting}
                data-testid="toggle-status"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const EditAdminForm = useMemo(
    () =>
      function EditAdminForm({
        formData,
        errors,
        isSubmitting,
        adminId,
        createdAt,
        handleNameChange,
        handlePhoneChange,
        handleAddress1Change,
        handleAddress2Change,
        handleCityChange,
        handleStateChange,
        handleZipChange,
      }: EditAdminFormProps) {
        return (
          <>
            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Podstawowe dane
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4">
                  <div className="flex-1 min-w-0">
                    <TextField
                      fullWidth={true}
                      label="Imię i nazwisko"
                      maxLength={200}
                      value={formData.name}
                      onChange={handleNameChange}
                      error={Boolean(errors.name)}
                      errorMessage={errors.name}
                      autoFocus={true}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <TextField
                      fullWidth={true}
                      label="Admin ID"
                      maxLength={200}
                      value={adminId}
                      disabled={true}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <TextField
                      fullWidth={true}
                      label="Adres e-mail"
                      maxLength={100}
                      value={formData.email}
                      disabled={true}
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-1 min-w-0">
                    <TextField
                      fullWidth={true}
                      label="Numer telefonu"
                      type="tel"
                      maxLength={9}
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      error={Boolean(errors.phone)}
                      errorMessage={errors.phone}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <TextField
                      fullWidth={true}
                      label="Data utworzenia"
                      maxLength={100}
                      value={createdAt}
                      disabled={true}
                    />
                  </div>
                  <div className="flex-1 min-w-0" />
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Dane adresowe
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4">
                  <div className="flex-1 min-w-0">
                    <TextField
                      fullWidth={true}
                      label="Adres (linia 1)"
                      maxLength={200}
                      value={formData.address1}
                      onChange={handleAddress1Change}
                      error={Boolean(errors.address1)}
                      errorMessage={errors.address1}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <TextField
                      fullWidth={true}
                      label="Adres (linia 2)"
                      maxLength={200}
                      value={formData.address2}
                      onChange={handleAddress2Change}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <TextField
                      fullWidth={true}
                      label="Miejscowość"
                      maxLength={200}
                      value={formData.city}
                      onChange={handleCityChange}
                      error={Boolean(errors.city)}
                      errorMessage={errors.city}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-1 min-w-0">
                    <TextField
                      fullWidth={true}
                      label="Województwo"
                      maxLength={200}
                      value={formData.state}
                      onChange={handleStateChange}
                      error={Boolean(errors.state)}
                      errorMessage={errors.state}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <TextField
                      fullWidth={true}
                      label="Kod pocztowy"
                      maxLength={200}
                      value={formData.zip}
                      onChange={handleZipChange}
                      error={Boolean(errors.zip)}
                      errorMessage={errors.zip}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <TextField
                      fullWidth={true}
                      label="Kraj"
                      maxLength={200}
                      value={formData.country}
                      disabled={true}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Dostęp i uprawnienia
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4 w-[32%]">
                  <TextField
                    fullWidth={true}
                    label="Rola"
                    maxLength={200}
                    disabled={true}
                    value={translateRole(formData.role)}
                  />
                </div>
              </div>
            </div>
          </>
        );
      },
    []
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-white h-screen w-full">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <CreateLayout
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        isCancelModalOpen={isCancelModalOpen}
        handleCloseCancelModal={handleCloseCancelModal}
        handleConfirmCancel={handleConfirmCancel}
        isSnackbarOpen={isSnackbarOpen}
        handleCloseSnackbar={handleCloseSnackbar}
        snackbarError={snackbarError}
        Header={EditAdminHeader}
        submitButtonLabel="Zapisz"
        submitButtonLoadingLabel="Saving..."
        submitButtonDisabled={isSubmitting || !hasChanges}
      >
        <EditAdminForm
          formData={formData}
          errors={errors}
          isSubmitting={isSubmitting}
          adminId={adminId}
          createdAt={createdAt}
          handleNameChange={
            handleNameChange as (
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => void
          }
          handlePhoneChange={
            handlePhoneChange as (
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => void
          }
          handleAddress1Change={
            handleAddress1Change as (
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => void
          }
          handleAddress2Change={
            handleAddress2Change as (
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => void
          }
          handleCityChange={
            handleCityChange as (
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => void
          }
          handleStateChange={
            handleStateChange as (
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => void
          }
          handleZipChange={
            handleZipChange as (
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => void
          }
        />
      </CreateLayout>

      {/* Status Update Dialog */}
      <UpdateStatusDialog
        open={isStatusDialogOpen}
        onClose={handleCloseStatusDialog}
        onConfirm={handleConfirmStatusUpdate}
        isSubmitting={isStatusSubmitting}
        {...getAdminStatusDialogConfig(formData.name, status === 'active')}
      />

      {/* Status Update Success Snackbar */}
      <Snackbar
        open={showStatusSuccessSnackbar}
        message={statusSuccessMessage}
        variant="success"
        onClose={handleCloseStatusSuccessSnackbar}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton={true}
      />

      {/* Status Update Error Snackbar */}
      <Snackbar
        open={showStatusErrorSnackbar}
        message={statusErrorMessage}
        variant="error"
        onClose={handleCloseStatusErrorSnackbar}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton={true}
      />
    </>
  );
};
export default EditAdmin;
